import React from "react";
import { Link as RouterLink } from "react-router";
import isEmpty from "lodash/isEmpty";
import snakeCase from "lodash/snakeCase";
import Link from "@mui/material/Link";

import { COLORS_OPTIONS } from "../colors";
import DimensionsField from "../../components/TablePro/fields/DimensionsField";
import ArrayField from "../../components/TablePro/fields/ArrayField";
import AttachmentsField from "../../components/TablePro/fields/assets/AttachmentsField";
import Attachments from "../../components/TablePro/fields/assets/Attachments";
import ImageButtonComponent from "../../components/TablePro/components/assets/ImageButton";
import InventoryTransactionField from "../../components/TablePro/fields/assets/InventoryTransactionField";
import labels from "../displayFields";
import AutocompleteField from "../../components/TablePro/fields/AutocompleteField";
import manufacturers from "../../constants/manufacturers";
import EditAssetModal from "../../components/TablePro/components/assets/EditAssetModal";
import AssetLinkComponent from "../../components/TablePro/components/assets/AssetLink";
import DropdownView from "../../components/TablePro/components/assets/DropdownView";
import RackLocationsComponent from "../../views/capture/RackLocations/scrubber/RackLocationsComponent";
import RackLocationsInput from "../../views/capture/RackLocations/scrubber/RackLocationsInput";
import ImageInput from "../../components/TablePro/fields/ImageInput";
import ImageComponent from "../../components/TablePro/fields/ImageComponent";
import MultipleDestinationRoomNumberComponent from "../../components/TablePro/fields/assets/MultipleDestinationRoomNumberComponent";
import MultipleDestinationRoomNumberInput from "../../components/TablePro/fields/assets/MultipleDestinationRoomNumberInput";
import dispositions from "../../constants/dispositions";
import CategoryField from "../../components/TablePro/fields/CategoryField";
import SubcategoryField from "../../components/TablePro/fields/SubcategoryField";
import SelectField from "../../components/TablePro/fields/SelectField";
import { predefinedCubicFeetOptions } from "../../constants/asset";
import BillingTypeField from "../../components/TablePro/fields/assets/BillingTypeField";
import DateOnly from "../../components/TablePro/fields/DateOnly";
import { palletTypeOptions } from "../../constants/receiving-wizard";
import { getActiveSortedPalletTypes } from "../../utils/palletHelpers";
import { getActiveSortedUnitTypes } from "../../utils/unitHelpers";
import { getSquareFootageTypeOptions } from "../../utils/squareFootageHelpers";
import ReceivingReportNumberComponent from "../../components/TablePro/components/assets/ReceivingReportNumberComponent";
import ExpirationDateInput from "../../components/TablePro/fields/ExpirationDateInput";
import MonthDayYearInput from "../../components/TablePro/fields/MonthDayYearInput";
import TableProMonthPicker from "../../components/TablePro/fields/TableProMonthPicker";
import TableProYearPicker from "../../components/TablePro/fields/TableProYearPicker";
import OutboundSummaryComponent from "../../components/TablePro/components/assets/OutboundSummaryComponent";
import CostCenterComponent from "../../components/TablePro/components/assets/CostCenterComponent";
import InboundSummaryModal from "../../components/InboundSummaryModal";
import unitOfMeasureOptions from "../fields/unitOfMeasure";
import OutboundSummaryModal from "../../components/OutboundSummaryModal";
import CostCenterSelectField from "../../components/TablePro/fields/CostCenterSelectField";
import SerialNumbersEditorModal from "../../components/form/SerialNumbersEditorModal";
import { formatDays } from "../../utils/dates";

const label = fieldId => labels[snakeCase(fieldId)];

export const multipleDestinationRoomNumber = {
  attrName: "rackLocations",
  input: MultipleDestinationRoomNumberInput,
  component: MultipleDestinationRoomNumberComponent,
};

export const AssetLink = ({ entity, id, value }) => (
  <Link
    className="unfocus"
    id={`assetLink-${id}-${entity.id}`}
    to={`/assets/${entity.id}`}
    component={RouterLink}
  >
    {value || ""}
  </Link>
);

const Dimension = ({ value }) => {
  if (!value) return null;
  return (
    <span>{value.map(field => [field.width, field.depth, field.height].join("x")).join(", ")}</span>
  );
};

const ColorsField = props => <AutocompleteField {...props} options={COLORS_OPTIONS} />;

const ArrayComponent = ({ value }) => (isEmpty(value) ? "" : value.join(", "));

const WarehousesFromRackLocationsComponent = ({ entity }) => {
  const warehouses = entity?.warehousesFromRackLocations;
  if (!warehouses || warehouses.length === 0) return "";
  return warehouses.map(warehouse => warehouse.name).join(", ");
};

const ExpirationMonthDayYearComponent = ({ value }) => {
  if (!value || value === "N/A") return null;
  return <span>{value}</span>;
};

const DaysLeftToExpireComponent = ({ value }) => {
  if (!value) return null;
  return <span>{formatDays(value)}</span>;
};

const splitSerials = value =>
  String(value || "")
    .split(",")
    .map(v => v.trim())
    .filter(Boolean);

const joinSerials = serials => serials.map(v => String(v || "").trim()).filter(Boolean).join(", ");

const SerialNumberSummaryComponent = ({ value }) => {
  const serials = splitSerials(value);

  if (serials.length === 0) {
    return "";
  }

  return (
    <div style={{ width: "100%", textAlign: "left" }}>
      {serials.map((serial, index) => (
        <div key={`${serial}-${index}`}>{serial}</div>
      ))}
    </div>
  );
};

const SerialNumberSummaryModalInput = ({ value, onSubmit, onClose }) => (
  <SerialNumbersEditorModal
    open
    onClose={onClose}
    initialSerials={splitSerials(value)}
    onSave={nextSerials => {
      onSubmit(joinSerials(nextSerials));
    }}
  />
);

export default {
  size: "large",
  limit: 45,
  order: {},
  version: "v10",
  fields: [
    {
      id: "BULK_CHECKBOX",
      label: "Checkbox Column",
      alwaysVisible: true,
      system: true,
      visible: true,
      isFrozen: true,
      unEditable: true,
      disableOrder: true,
      width: 100,
    },
    {
      id: "photoUpload",
      attrName: "photo",
      label: "Image Upload",
      width: 90,
      alwaysVisible: true,
      visible: true,
      disableOrder: true,
      disableFillDown: true,
      unEditable: true,
      component: ImageButtonComponent,
    },
    {
      id: "client.name",
      label: "Client",
      visible: true,
    },
    {
      id: "project.name",
      label: "Project",
      visible: true,
    },
    {
      id: "itemId",
      label: label("itemId"),
      component: AssetLinkComponent,
      input: EditAssetModal,
      visible: true,
    },
    {
      id: "photoCarousel",
      label: "Image Viewer",
      alwaysVisible: true,
      attrName: "photo",
      visible: true,
      disableOrder: true,
      component: ImageComponent,
      input: ImageInput,
    },
    {
      id: "disposition",
      label: label("disposition"),
      visible: false,
      type: "autocomplete",
      component: DropdownView,
      input: props => <AutocompleteField {...props} options={dispositions.map(v => [v, v])} />,
    },
    {
      id: "minimumQuantity",
      label: label("minimumQuantity"),
      visible: false,
      inputType: "number",
    },
    {
      id: "existingBuilding",
      label: label("existingBuilding"),
      visible: false,
    },
    {
      id: "quantity",
      label: label("quantity"),
      inputType: "number",
      visible: true,
      width: 140,
    },
    {
      id: "availableQuantity",
      label: label("availableQuantity"),
      visible: false,
      width: 140,
      disableOrder: true,
      disableFillDown: true,
      input: InventoryTransactionField,
      // When onsite inventory capture is enabled, show this as view-only
      // whenever Origin Quantity (`quantity`) is displayed.
      onsiteInventoryCapture: {
        showWhenFieldDisplayed: "quantity",
        viewOnly: true,
      },
    },
    {
      id: "newBuilding",
      label: label("newBuilding"),
      visible: false,
      isFrozen: true,
    },
    {
      id: "color",
      label: label("color"),
      type: "autocomplete",
      input: ColorsField,
      component: DropdownView,
      visible: true,
    },
    {
      id: "existingFloor",
      label: label("existingFloor"),
      visible: false,
    },
    {
      id: "newFloor",
      label: label("newFloor"),
      visible: false,
    },
    {
      id: "existingRoom",
      label: label("existingRoom"),
      visible: false,
    },
    {
      id: "newRoom",
      label: label("newRoom"),
      visible: false,
    },
    {
      id: "barcode",
      label: label("barcode"),
      component: AssetLinkComponent,
      input: EditAssetModal,
      visible: false,
    },
    {
      id: "description",
      label: label("description"),
      visible: true,
    },
    {
      id: "descriptionComments",
      label: label("descriptionComments"),
      visible: false,
    },
    {
      id: "dimensions",
      label: label("dimensions"),
      visible: true,
      input: DimensionsField,
      component: Dimension,
      disableOrder: true,
      initialValue: [],
    },
    {
      id: "manufacturer",
      label: label("manufacturer"),
      visible: true,
      type: "autocomplete",
      component: DropdownView,
      input: props => <AutocompleteField {...props} options={manufacturers.map(v => [v, v])} />,
    },
    {
      id: "condition",
      label: label("condition"),
      visible: false,
    },
    {
      id: "finishes",
      label: label("finishes"),
      visible: false,
      input: ArrayField,
      component: ArrayComponent,
    },
    {
      id: "originComments",
      label: label("originComments"),
      visible: false,
    },
    {
      id: "destinationComments",
      label: label("destinationComments"),
      visible: false,
    },
    {
      id: "originEmployeeName",
      label: label("originEmployeeName"),
      visible: false,
    },
    {
      id: "destinationEmployeeName",
      label: label("destinationEmployeeName"),
      visible: false,
    },
    {
      id: "destinationQuantity",
      label: label("destinationQuantity"),
      visible: false,
      inputType: "number",
    },
    {
      id: "originRoomNumber",
      label: label("originRoomNumber"),
      visible: false,
    },
    {
      id: "destinationRoomNumber",
      label: label("destinationRoomNumber"),
      visible: false,
    },
    {
      id: "originCubbyNumber",
      label: label("originCubbyNumber"),
      visible: false,
    },
    {
      id: "model",
      label: label("model"),
      visible: true,
    },
    {
      id: "rackLocations",
      label: label("rackLocations"),
      component: RackLocationsComponent,
      input: RackLocationsInput,
      visible: false,
    },
    // MEDICAL FIELDS
    {
      id: "departmentName",
      label: label("departmentName"),
      visible: false,
    },
    {
      id: "serialNumber",
      label: label("serialNumber"),
      visible: false,
      component: SerialNumberSummaryComponent,
      input: SerialNumberSummaryModalInput,
      openOnSingleClick: true,
    },
    {
      id: "destinationNumber",
      label: label("destinationNumber"),
      visible: false,
    },
    {
      id: "installResponsibility",
      label: label("installResponsibility"),
      visible: false,
    },
    {
      id: "attachments",
      label: label("attachments"),
      visible: false,
      disableFillDown: true,
      input: AttachmentsField,
      component: Attachments,
      disableOrder: true,
    },
    {
      id: "quantityOrdered",
      label: "Quantity Ordered",
      visible: false,
      inputType: "number",
    },
    {
      id: "reservedQuantity",
      label: "Reserved Quantity",
      visible: false,
      input: ({ value }) => value,
    },
    {
      id: "poNumber",
      label: "PO Number",
      visible: false,
      inputType: "text",
    },
    {
      id: "idCostCenter",
      attrName: "costCenterId",
      label: labels.cost_center,
      visible: false,
      component: CostCenterComponent,
      input: CostCenterSelectField,
    },
    {
      id: "unitCost",
      label: labels.unit_cost,
      visible: false,
      inputType: "number",
    },
    {
      id: "itemCode",
      label: labels.item_code,
      visible: false,
    },
    {
      id: "weight",
      label: label("weight"),
      visible: false,
    },
    {
      id: "assetTotalWeight",
      label: label("asset_total_weight"),
      visible: false,
    },
    {
      id: "category",
      label: "Category",
      visible: false,
      type: "autocomplete",
      component: DropdownView,
      input: CategoryField,
    },
    {
      id: "subcategory",
      label: "Subcategory",
      visible: false,
      type: "autocomplete",
      component: DropdownView,
      input: SubcategoryField,
    },
    {
      id: "warehouseQuantity",
      label: "Quantity in Warehouse",
      visible: false,
      unEditable: true,
    },
    {
      id: "warehousesFromRackLocations",
      label: "Current Warehouse",
      visible: false,
      unEditable: true,
      component: WarehousesFromRackLocationsComponent,
    },
    {
      id: "allocatedQuantity",
      label: "Quantity to Be Picked",
      visible: false,
      unEditable: true,
    },
    {
      id: "lastIn",
      label: "Last In",
      visible: false,
      component: DateOnly,
      input: props => <MonthDayYearInput {...props} label="Last In" submitFormat="YYYY-MM-DD" />,
    },
    {
      id: "lastOut",
      label: "Last Out",
      visible: false,
      unEditable: true,
      component: DateOnly,
    },
    {
      id: "inCartQuantity",
      label: "Quantity in Cart",
      visible: false,
      unEditable: true,
    },
    {
      id: "receivingReportNumber",
      label: "Inbound Summary",
      visible: false,
      component: ReceivingReportNumberComponent,
      input: ({ entity, onClose }) => <InboundSummaryModal asset={entity} onClose={onClose} />,
    },
    {
      id: "outboundSummary",
      label: "Outbound Summary",
      visible: false,
      component: OutboundSummaryComponent,
      input: ({ entity, onClose }) => <OutboundSummaryModal asset={entity} onClose={onClose} />,
    },
    {
      id: "palletType",
      label: label("palletType"),
      visible: false,
      type: "autocomplete",
      component: ({ value, entity }) => {
        const options = entity?.project?.palletTypes
          ? getActiveSortedPalletTypes(entity.project.palletTypes)
              .filter(pt => pt.id)
              .map(pt => [pt.id, pt.name])
          : palletTypeOptions;
        const match = options.find(([id]) => id === value);
        return <DropdownView value={match ? match[1] : value} />;
      },
      input: ({ entity, ...props }) => {
        const options = entity?.project?.palletTypes
          ? getActiveSortedPalletTypes(entity.project.palletTypes)
              .filter(pt => pt.id)
              .map(pt => [pt.id, pt.name])
          : palletTypeOptions;
        return <SelectField {...props} options={options} />;
      },
    },
    {
      id: "unitType",
      label: label("unitType"),
      visible: false,
      type: "autocomplete",
      component: ({ value, entity }) => {
        const options = entity?.project?.unitTypes
          ? getActiveSortedUnitTypes(entity.project.unitTypes)
              .filter(ut => ut.id)
              .map(ut => [ut.id, ut.name])
          : [["standard_unit", "Standard Unit"]];
        const match = options.find(([id]) => id === value);
        return <DropdownView value={match ? match[1] : value} />;
      },
      input: ({ entity, ...props }) => {
        const options = entity?.project?.unitTypes
          ? getActiveSortedUnitTypes(entity.project.unitTypes)
              .filter(ut => ut.id)
              .map(ut => [ut.id, ut.name])
          : [["standard_unit", "Standard Unit"]];
        return <SelectField {...props} options={options} />;
      },
    },
    {
      id: "squareFootageType",
      label: label("squareFootageType"),
      visible: false,
      type: "autocomplete",
      component: ({ value, entity }) => {
        const options = getSquareFootageTypeOptions(entity?.project?.squareFootageTypes);
        const match = options.find(([id]) => id === value);
        return <DropdownView value={match ? match[1] : value} />;
      },
      input: ({ entity, ...props }) => (
        <SelectField
          {...props}
          options={getSquareFootageTypeOptions(entity?.project?.squareFootageTypes)}
        />
      ),
    },
    {
      id: "createdAt",
      label: "Created On",
      visible: true,
      unEditable: true,
      component: DateOnly,
    },
    {
      id: "updatedAt",
      label: "Updated On",
      visible: true,
      unEditable: true,
      component: DateOnly,
    },
    {
      id: "osdNotes",
      label: label("osd_notes"),
      visible: false,
      unEditable: true,
    },
    {
      id: "generalNotes",
      label: label("general_notes"),
      visible: false,
    },

    {
      id: "inventoryNumber",
      label: "Inventory Number",
      visible: false,
    },
    {
      id: "skuNumber",
      label: "SKU Number",
      visible: false,
    },
    {
      id: "batchNumber",
      label: "Batch/Lot Number",
      visible: false,
    },
    {
      id: "billingType",
      label: "Billing Type",
      visible: false,
      type: "autocomplete",
      component: DropdownView,
      input: BillingTypeField,
    },
    {
      id: "manufacturePartNumber",
      label: "Manufacture Part Number",
      visible: false,
    },
    {
      id: "unitOfMeasure",
      label: "Unit of Measure",
      visible: false,
      type: "autocomplete",
      component: DropdownView,
      input: props => (
        <AutocompleteField
          {...props}
          options={unitOfMeasureOptions.map(opt => [opt.value, opt.label])}
        />
      ),
    },
    {
      id: "expirationDate",
      label: "Expiration Date (MM/YYYY)",
      visible: false,
      input: ExpirationDateInput,
    },
    {
      id: "expirationMonthDayYear",
      label: "Expiration Date (MM/DD/YYYY)",
      visible: false,
      component: ExpirationMonthDayYearComponent,
      input: props => <MonthDayYearInput {...props} />,
    },
    {
      id: "manufactureDate",
      label: label("manufactureDate"),
      visible: false,
      component: ExpirationMonthDayYearComponent,
      input: props => <MonthDayYearInput {...props} />,
    },
    {
      id: "expirationMonth",
      label: "Expiration Month",
      visible: false,
      input: TableProMonthPicker,
    },
    {
      id: "expirationYear",
      label: "Expiration Year",
      visible: false,
      input: TableProYearPicker,
    },
    {
      id: "daysLeftToExpire",
      label: label("daysLeftToExpire"),
      visible: false,
      unEditable: true,
      component: DaysLeftToExpireComponent,
    },
    {
      id: "squareFootage",
      label: "Square Footage",
      visible: false,
    },
    {
      id: "cubicFootage",
      label: "Cubic Footage",
      visible: false,
    },
    {
      id: "predefinedCubicFeet",
      label: label("predefinedCubicFeet"),
      visible: false,
      type: "autocomplete",
      component: DropdownView,
      input: props => <SelectField {...props} options={predefinedCubicFeetOptions} />,
    },
    {
      id: "carrierName",
      label: "Carrier Name",
      visible: false,
    },
    {
      id: "orderNumber",
      label: "Order Number",
      visible: false,
    },
    {
      id: "bolNumber",
      label: "BOL Number",
      visible: false,
    },
    {
      id: "trackingNumber",
      label: "Tracking Number",
      visible: false,
    },
    {
      id: "truckNumber",
      label: "Truck Number",
      visible: false,
    },
  ],
};
